import * as v from 'valibot'

// Valibot duplicate of `Profile` from the `shared` package. Used in the root
// route's `validateSearch` so that parsing URL search params doesn't pull the
// zod schema (and the entire zod runtime) into the entry chunk.
//
// Keep this file in sync with `apps/shared/src/shared.ts` (`Profile`,
// `Filters`, `SiteInferProfile`, `TeamProfileEvaluation`, `Site`).

const Site = v.picklist([
  'universal',
  'linkedin',
  'infojobs',
  'buscojobs',
  'manpower',
  'randstad',
  'tecnoempleo',
  'talentclue',
])

const Sex = v.picklist(['male', 'female', 'neutral'])

// Exported so the root search schema's `collectiveFilters` can pick its subset from here rather than
// re-typing the bounds a third time (zod, this file, __root.tsx). See `CollectiveFilters` in shared.ts,
// which picks the same keys off the zod `Filters`.
export const FiltersShape = {
  sex: v.fallback(v.optional(v.array(Sex)), undefined),
  ageMin: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(120))),
  ageMax: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(120))),
  peopleInCharge: v.optional(v.array(v.boolean())),
  level: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(5)))),
  function: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(34)))),
  sector: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(147)))),
  employees: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(8)))),
  educationalLevel: v.optional(v.array(v.pipe(v.number(), v.minValue(0), v.maxValue(6)))),
  country: v.optional(v.array(v.string())),
  ids: v.optional(v.array(v.number())),
  filtersOrder: v.optional(v.array(v.string())),
} as const

const SiteInferProfileShape = {
  site: v.fallback(v.optional(Site), undefined),
  name: v.nullish(v.string()),
  levelText: v.optional(v.string()),
  functionText: v.optional(v.string()),
  sectorText: v.optional(v.string()),
  ids: v.optional(v.array(v.number())),
} as const

const TeamProfileEvaluationShape = {
  teamId: v.optional(v.number()),
  evaluation: v.fallback(v.optional(v.picklist(['owns', 'ownsManual', 'team', 'manual'])), undefined),
  members: v.optional(v.array(v.number())),
  name: v.nullish(v.string()),
} as const

// Mirrors `answerSchema` / `answersSchema` from `shared.ts`: 36 answers, each a permutation of
// `[0, 1, 2]` (the ranking the candidate gave three options).
//
// Three digits plus one distinctness check, rather than a union of the six permutations. The union
// cost 12.3% of server CPU on `/report`: valibot tries the arms in order, so an answer failed ~3.5 of
// them before matching, and EVERY failed arm allocated an issue and stringified the received value
// (`_addIssue` + `_stringify` alone were 5.2%). With 108 answers in a profile and `validateSearch`
// running several times per request, that is ~380 discarded issue objects per pass.
//
// The answer stays a `v.tuple`, matching the old shape exactly rather than a faster hand-written
// predicate: `v.tuple` ACCEPTS extra items and drops them, so a `[0, 1, 2, 0]` answer used to
// normalise and render, and a strict-length predicate turns that into a 500. Fuzzing 4,000 malformed
// inputs, this form agrees with the old schema on accept/reject AND on output. It parses a 36-answer
// set 5.7x faster (22.16µs -> 3.89µs).
const answerDigit = v.picklist([0, 1, 2])

const answerSchema = v.pipe(
  v.tuple([answerDigit, answerDigit, answerDigit]),
  v.check(
    (answer) => answer[0] !== answer[1] && answer[1] !== answer[2] && answer[0] !== answer[2],
    'Expected a permutation of [0, 1, 2]',
  ),
)

// Exactly 36 answers, but tolerant of a longer list the way the 36-element tuple it replaces was:
// `v.tuple` accepted extra items and dropped them, so a 37-answer URL used to normalise and render.
// An array with a plain `v.length(36)` would turn that into a 500, so keep the old shape: require at
// least 36, then trim. An array (not a tuple) because the zod mirror in shared.ts is one, and the two
// MUST infer the same type or the router's search types stop matching the ones the route tree was
// generated against (TS2719).
const answersSchema = v.pipe(
  v.array(answerSchema),
  v.minLength(36),
  v.transform((answers) => (answers.length === 36 ? answers : answers.slice(0, 36))),
)

export const Profile = v.variant('type', [
  v.object({ type: v.literal('employee'), id: v.number() }),
  v.object({
    type: v.literal('market_company'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_company_job'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({ type: v.literal('applicant'), id: v.optional(v.number()) }),
  v.object({
    type: v.literal('job'),
    id: v.number(),
    source: v.fallback(v.optional(v.picklist(['described', 'integrants'])), undefined),
    ...SiteInferProfileShape,
  }),
  v.object({ type: v.literal('team'), id: v.number(), ...TeamProfileEvaluationShape }),
  v.object({ type: v.literal('member'), id: v.number(), ...TeamProfileEvaluationShape }),
  v.object({ type: v.literal('org_group'), id: v.string() }),
  v.object({ type: v.literal('org_user'), id: v.string() }),
  v.object({
    type: v.literal('market'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_applicant'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('market_job'),
    id: v.optional(v.number()),
    ...FiltersShape,
    ...SiteInferProfileShape,
  }),
  v.object({
    type: v.literal('public'),
    id: v.optional(v.number()),
    name: v.optional(v.string()),
    competences: v.optional(answersSchema),
    values: v.optional(answersSchema),
    personality: v.optional(answersSchema),
  }),
])

export type Profile = v.InferOutput<typeof Profile>
